import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { UserPlus, ShieldCheck, Clock, Trash2, Infinity as InfinityIcon, Save, Pencil, X, Check } from "lucide-react";

// ============ ADMIN USERS MANAGEMENT (multi-admin + time-limited + perms) ============
const PERM_LABELS = {
  stock_count: { label: "📦 Stok Sayımı", desc: "Barkod oku + sayım gir + kendi Excel" },
  products_view: { label: "🧾 Ürünleri Gör", desc: "Ürünler sekmesi görünür" },
  products_edit: { label: "✏️ Ürün Düzenle", desc: "Ekle / sil / resim / fiyat" },
  orders: { label: "📋 Siparişler", desc: "Siparişleri gör / durum değiştir" },
  customers: { label: "👥 Müşteriler", desc: "Müşteri paneli" },
  courier: { label: "🛵 Kurye", desc: "Kurye paneli" },
  marketing: { label: "📣 Pazarlama", desc: "İndirim talebi + çekiliş" },
  reports: { label: "📊 Raporlar", desc: "Günlük rapor" },
};
const DEFAULT_PERMS = { stock_count: true, products_view: false, products_edit: false, orders: false, customers: false, courier: false, marketing: false, reports: false };
const EMPTY_PERMS = { stock_count: false, products_view: false, products_edit: false, orders: false, customers: false, courier: false, marketing: false, reports: false };

export default function AdminsPanel() {
  const [data, setData] = useState({ items: [], can_manage: false, total: 0 });
  const [loading, setLoading] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newHours, setNewHours] = useState("");
  const [newNote, setNewNote] = useState("");
  const [newPerms, setNewPerms] = useState({ ...DEFAULT_PERMS });
  const [submitting, setSubmitting] = useState(false);
  const [nowTick, setNowTick] = useState(Date.now());
  const [expandedEmail, setExpandedEmail] = useState(null);

  const togglePerm = (k) => setNewPerms((p) => ({ ...p, [k]: !p[k] }));
  const setAllPerms = (v) => setNewPerms(Object.fromEntries(Object.keys(DEFAULT_PERMS).map((k) => [k, v])));

  // 1-second countdown tick — only runs while this panel is visible.
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/admins");
      setData(r.data || { items: [], can_manage: false, total: 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const addAdmin = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) {
      toast.error("Geçerli bir email gir");
      return;
    }
    const hours = newHours.trim() ? parseFloat(newHours) : null;
    if (newHours.trim() && (isNaN(hours) || hours <= 0)) {
      toast.error("Saat pozitif bir sayı olmalı (örn: 24, 72, 168)");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/admin/admins", {
        email,
        expires_in_hours: hours,
        note: newNote.trim(),
        permissions: newPerms,
      });
      toast.success(hours
        ? `✓ ${email} eklendi · ⏱ ${hours} saat sonra sona erer`
        : `✓ ${email} kalıcı admin olarak eklendi`);
      setNewEmail(""); setNewHours(""); setNewNote(""); setNewPerms({ ...DEFAULT_PERMS });
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eklenemedi");
    } finally {
      setSubmitting(false);
    }
  };

  const extend = async (email, hours) => {
    try {
      await api.post(`/admin/admins/${encodeURIComponent(email)}/extend`, {
        expires_in_hours: hours,
      });
      toast.success(hours === null
        ? `✓ ${email} artık kalıcı`
        : `✓ ${email} ${hours} saat uzatıldı`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Uzatılamadı");
    }
  };

  const remove = async (email) => {
    if (!window.confirm(`${email} admin listesinden kaldırılsın mı?`)) return;
    try {
      await api.delete(`/admin/admins/${encodeURIComponent(email)}`);
      toast.success(`${email} kaldırıldı`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaldırılamadı");
    }
  };

  const savePermissions = async (email, perms) => {
    try {
      await api.put(`/admin/admins/${encodeURIComponent(email)}/permissions`, {
        permissions: perms,
      });
      toast.success(`✓ ${email} yetkileri güncellendi`);
      setExpandedEmail(null);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Yetkiler güncellenemedi");
      throw e;
    }
  };

  const items = useMemo(() => data.items || [], [data]);

  return (
    <div className="space-y-4">
      <div className="bg-[#2E5A36]/5 border border-[#2E5A36]/20 rounded-2xl p-4">
        <h3 className="font-heading text-lg font-bold text-[#1B2A22] mb-1 flex items-center gap-2">
          <ShieldCheck className="w-5 h-5" /> Yöneticiler ({data.total})
        </h3>
        <p className="text-xs text-[#1B2A22]/70">
          <b>Birincil admin</b> (env'den) ve <b>sabit ikincil admin</b> (<code>ikizlermarketbt@gmail.com</code>) hiç kaldırılamaz —
          güvenlik için en az biri her zaman erişime sahip olmalı.
          <br />
          <b>Süreli admin</b>: Belirli bir saat girersen o süre dolunca otomatik olarak admin yetkisi kaybolur. Hemen panelden çıkartman gerekmez.
          <br />
          <b>🛡️ Yetkiler</b>: Her personelin yetkileri <u>ayrı</u> tutulur. Satırdaki <b>Yetkileri Düzenle</b> butonuyla sonradan değiştirip <b>Kaydet</b>'e bas.
          {!data.can_manage && (
            <>
              <br />
              <span className="text-[#D9653B] font-heading font-bold">⚠️ Yalnızca kalıcı yöneticiler bu listeyi değiştirebilir.</span>
            </>
          )}
        </p>
      </div>

      {/* Add admin form — only visible to permanent admins */}
      {data.can_manage && (
        <div className="bg-white border border-[#E8E2D5] rounded-2xl p-4 space-y-3">
          <h4 className="font-heading font-bold text-[#1B2A22] flex items-center gap-2">
            <UserPlus className="w-4 h-4" /> Yeni Yönetici Ekle
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <Label className="text-xs">E-posta adresi</Label>
              <Input
                data-testid="new-admin-email"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="ornek@gmail.com"
                className="bg-white font-mono"
              />
            </div>
            <div>
              <Label className="text-xs">Süre (saat)</Label>
              <Input
                data-testid="new-admin-hours"
                type="number"
                step="1"
                min="0"
                value={newHours}
                onChange={(e) => setNewHours(e.target.value)}
                placeholder="Boş = kalıcı"
                className="bg-white font-mono"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Not (opsiyonel)</Label>
            <Input
              data-testid="new-admin-note"
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="örn: Depo elemanı, hafta sonu için"
              className="bg-white"
            />
          </div>
          <div className="flex gap-2">
            <Button
              data-testid="new-admin-permanent-btn"
              onClick={() => { setNewHours(""); addAdmin(); }}
              disabled={submitting || !newEmail}
              variant="outline"
              className="rounded-full border-[#2E5A36] text-[#2E5A36] font-heading font-bold"
            >
              <InfinityIcon className="w-4 h-4 mr-1" /> Kalıcı Ekle
            </Button>
            <Button
              data-testid="new-admin-timed-btn"
              onClick={addAdmin}
              disabled={submitting || !newEmail}
              className="bg-[#D9653B] hover:bg-[#B4522F] text-white rounded-full font-heading font-bold"
            >
              <Clock className="w-4 h-4 mr-1" /> {newHours ? `${newHours} Saat Erişim` : "Ekle"}
            </Button>
          </div>
          <p className="text-[11px] text-[#1B2A22]/60">
            Hızlı seçim:{" "}
            {[8, 24, 72, 168, 720].map((h) => (
              <button
                key={h}
                type="button"
                onClick={() => setNewHours(String(h))}
                className="inline-block mr-1 bg-[#E5A024]/10 hover:bg-[#E5A024]/25 text-[#B4711C] rounded-full px-2 py-0.5 text-[11px] font-mono"
              >
                {h >= 24 ? `${Math.round(h / 24)} gün` : `${h} saat`}
              </button>
            ))}
          </p>

          {/* Permission checkboxes — başlangıç yetkileri */}
          <div className="pt-3 border-t border-[#E8E2D5]">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-heading font-bold">🛡️ Başlangıç Yetkileri</Label>
              <div className="flex gap-1">
                <button type="button" onClick={() => setAllPerms(true)} className="text-[10px] text-[#2E5A36] underline">Hepsi</button>
                <span className="text-[10px] text-[#1B2A22]/40">·</span>
                <button type="button" onClick={() => setAllPerms(false)} className="text-[10px] text-red-600 underline">Hiçbiri</button>
                <span className="text-[10px] text-[#1B2A22]/40">·</span>
                <button type="button" onClick={() => setNewPerms({ ...DEFAULT_PERMS })} className="text-[10px] text-[#B4711C] underline">Varsayılan</button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
              {Object.entries(PERM_LABELS).map(([k, meta]) => (
                <label
                  key={k}
                  className={`flex items-start gap-2 p-2 rounded-lg border cursor-pointer transition ${newPerms[k] ? "bg-[#2E5A36]/5 border-[#2E5A36]" : "bg-white border-[#E8E2D5] hover:border-[#2E5A36]/40"}`}
                >
                  <input
                    type="checkbox"
                    checked={!!newPerms[k]}
                    onChange={() => togglePerm(k)}
                    className="mt-1 w-4 h-4 accent-[#2E5A36]"
                    data-testid={`perm-${k}`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-heading font-bold text-xs text-[#1B2A22]">{meta.label}</p>
                    <p className="text-[10px] text-[#1B2A22]/60 leading-tight">{meta.desc}</p>
                  </div>
                </label>
              ))}
            </div>
            <p className="text-[10px] text-[#1B2A22]/60 mt-2">
              💡 <b>Önerilen</b>: Depo personeline sadece <code>Stok Sayımı</code> ver — barkod okutsun, miktar girsin, Excel sana düşsün.
              <br />
              Yetkileri her personel için <b>sonradan da</b> satırdaki <b>✏️ Yetkileri Düzenle</b> butonuyla ayrı ayrı değiştirebilirsin.
            </p>
          </div>
        </div>
      )}

      {/* Admin roster */}
      <div className="bg-white border border-[#E8E2D5] rounded-2xl overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>E-posta</TableHead>
              <TableHead>Tür</TableHead>
              <TableHead>Eklenme</TableHead>
              <TableHead>Son Kullanma</TableHead>
              <TableHead>Yetkiler</TableHead>
              {data.can_manage && <TableHead>İşlem</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={6} className="text-center py-6 text-[#1B2A22]/50">Yükleniyor...</TableCell></TableRow>
            )}
            {!loading && items.map((a) => (
              <AdminRow
                key={a.email}
                admin={a}
                nowTick={nowTick}
                canManage={data.can_manage}
                onExtend={extend}
                onRemove={remove}
                onSavePerms={savePermissions}
                expanded={expandedEmail === a.email}
                onToggleExpand={() => setExpandedEmail((cur) => cur === a.email ? null : a.email)}
              />
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function AdminRow({ admin, nowTick, canManage, onExtend, onRemove, onSavePerms, expanded, onToggleExpand }) {
  const expiresMs = admin.expires_at ? new Date(admin.expires_at).getTime() : null;
  const isExpired = expiresMs ? expiresMs < nowTick : false;
  const isPrimary = admin.is_primary;
  const isPinned = admin.is_permanent && !isPrimary;
  const isPermanent = isPrimary || isPinned;

  const typeBadge = isPrimary
    ? <span className="text-[10px] bg-[#2E5A36] text-white rounded-full px-2 py-0.5 font-heading font-bold">👑 BİRİNCİL</span>
    : isPinned
      ? <span className="text-[10px] bg-[#1B2A22] text-white rounded-full px-2 py-0.5 font-heading font-bold">📌 SABİT</span>
      : expiresMs
        ? (isExpired
            ? <span className="text-[10px] bg-red-600 text-white rounded-full px-2 py-0.5 font-heading font-bold">⏱ SÜRESİ DOLDU</span>
            : <span className="text-[10px] bg-[#D9653B] text-white rounded-full px-2 py-0.5 font-heading font-bold">⏱ SÜRELİ</span>)
        : <span className="text-[10px] bg-[#2E5A36]/70 text-white rounded-full px-2 py-0.5 font-heading font-bold">∞ KALICI</span>;

  const expiryText = (() => {
    if (!expiresMs) return <span className="text-[#2E5A36]/70 flex items-center gap-1"><InfinityIcon className="w-3 h-3" /> Süresiz</span>;
    if (isExpired) return <span className="text-red-600 font-mono text-xs">Sona erdi</span>;
    const diff = expiresMs - nowTick;
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    const d = Math.floor(h / 24);
    const label = d > 0 ? `${d}g ${h % 24}sa` : h > 0 ? `${h}sa ${m}dk` : m > 0 ? `${m}dk ${s}sn` : `${s}sn`;
    return (
      <span className={`font-mono text-xs ${h < 1 ? "text-red-600 font-bold" : h < 24 ? "text-[#D9653B]" : "text-[#1B2A22]"}`} data-testid={`admin-countdown-${admin.email}`}>
        {label} kaldı
      </span>
    );
  })();

  // Permission summary (compact chips)
  const perms = admin.permissions || {};
  const activePerms = Object.keys(PERM_LABELS).filter((k) => perms[k]);
  const permsSummary = isPermanent ? (
    <span className="text-[10px] bg-[#2E5A36] text-white rounded-full px-2 py-0.5 font-heading font-bold">✓ TÜM YETKİLER</span>
  ) : activePerms.length === 0 ? (
    <span className="text-[10px] text-red-600 font-mono">(yetki yok)</span>
  ) : (
    <div className="flex gap-1 flex-wrap max-w-[280px]">
      {activePerms.slice(0, 3).map((k) => (
        <span key={k} className="text-[10px] bg-[#2E5A36]/10 text-[#2E5A36] rounded-full px-1.5 py-0.5 font-heading font-bold" title={PERM_LABELS[k].desc}>
          {PERM_LABELS[k].label.replace(/^[^\s]+\s/, "")}
        </span>
      ))}
      {activePerms.length > 3 && (
        <span className="text-[10px] text-[#1B2A22]/60 font-mono">+{activePerms.length - 3}</span>
      )}
    </div>
  );

  return (
    <>
      <TableRow data-testid={`admin-row-${admin.email}`} className={isExpired ? "bg-red-50 opacity-60" : expanded ? "bg-[#E5A024]/5" : ""}>
        <TableCell className="font-mono text-xs font-bold align-top">
          {admin.email}
          {admin.note && <p className="text-[10px] text-[#1B2A22]/60 font-sans mt-0.5">{admin.note}</p>}
        </TableCell>
        <TableCell className="align-top">{typeBadge}</TableCell>
        <TableCell className="font-mono text-[10px] text-[#1B2A22]/60 align-top">
          {admin.added_at ? new Date(admin.added_at).toLocaleString("tr-TR") : "—"}
        </TableCell>
        <TableCell className="align-top">{expiryText}</TableCell>
        <TableCell className="align-top">{permsSummary}</TableCell>
        {canManage && (
          <TableCell className="align-top">
            {isPermanent ? (
              <span className="text-[10px] text-[#1B2A22]/40">🔒 kilitli</span>
            ) : (
              <div className="flex gap-1 flex-wrap">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onToggleExpand}
                  className={`h-7 px-2 rounded-full text-xs ${expanded ? "bg-[#E5A024] text-white border-[#E5A024]" : "text-[#B4711C] border-[#E5A024]"}`}
                  title="Yetkileri düzenle"
                  data-testid={`admin-edit-perms-${admin.email}`}
                >
                  {expanded ? <X className="w-3 h-3" /> : <Pencil className="w-3 h-3 mr-1" />}
                  {expanded ? "" : "Yetkiler"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onExtend(admin.email, 24)}
                  className="h-7 px-2 rounded-full text-xs"
                  title="24 saat uzat"
                  data-testid={`admin-extend-24-${admin.email}`}
                >
                  +24s
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onExtend(admin.email, null)}
                  className="h-7 px-2 rounded-full text-xs text-[#2E5A36] border-[#2E5A36]"
                  title="Süresiz yap"
                  data-testid={`admin-make-permanent-${admin.email}`}
                >
                  <InfinityIcon className="w-3 h-3" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onRemove(admin.email)}
                  className="h-7 px-2 rounded-full text-xs text-red-600 border-red-300 hover:bg-red-50"
                  title="Kaldır"
                  data-testid={`admin-remove-${admin.email}`}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            )}
          </TableCell>
        )}
      </TableRow>
      {expanded && canManage && !isPermanent && (
        <TableRow className="bg-[#E5A024]/5 border-y-2 border-[#E5A024]">
          <TableCell colSpan={6} className="p-0">
            <EditPermsEditor
              email={admin.email}
              initial={{ ...EMPTY_PERMS, ...(admin.permissions || {}) }}
              onCancel={onToggleExpand}
              onSave={onSavePerms}
            />
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

function EditPermsEditor({ email, initial, onCancel, onSave }) {
  const [perms, setPerms] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const toggle = (k) => {
    setPerms((p) => ({ ...p, [k]: !p[k] }));
    setDirty(true);
  };
  const setAll = (v) => {
    setPerms(Object.fromEntries(Object.keys(PERM_LABELS).map((k) => [k, v])));
    setDirty(true);
  };
  const reset = () => {
    setPerms({ ...DEFAULT_PERMS });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await onSave(email, perms);
    } catch {
      // toast already shown
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 bg-[#FAF7F2]">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-heading font-bold text-sm text-[#1B2A22] flex items-center gap-2">
          <Pencil className="w-4 h-4 text-[#E5A024]" />
          <span className="font-mono text-xs">{email}</span>
          <span className="text-[11px] text-[#1B2A22]/60">— yetkileri düzenle</span>
        </h4>
        <div className="flex gap-2">
          <button type="button" onClick={() => setAll(true)} className="text-[10px] text-[#2E5A36] underline">Hepsini aç</button>
          <span className="text-[10px] text-[#1B2A22]/30">·</span>
          <button type="button" onClick={() => setAll(false)} className="text-[10px] text-red-600 underline">Hepsini kapat</button>
          <span className="text-[10px] text-[#1B2A22]/30">·</span>
          <button type="button" onClick={reset} className="text-[10px] text-[#B4711C] underline">Varsayılan</button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-1 mb-3">
        {Object.entries(PERM_LABELS).map(([k, meta]) => (
          <label
            key={k}
            className={`flex items-start gap-2 p-2 rounded-lg border cursor-pointer transition ${perms[k] ? "bg-[#2E5A36]/5 border-[#2E5A36]" : "bg-white border-[#E8E2D5] hover:border-[#2E5A36]/40"}`}
          >
            <input
              type="checkbox"
              checked={!!perms[k]}
              onChange={() => toggle(k)}
              className="mt-1 w-4 h-4 accent-[#2E5A36]"
              data-testid={`edit-perm-${email}-${k}`}
            />
            <div className="flex-1 min-w-0">
              <p className="font-heading font-bold text-xs text-[#1B2A22]">{meta.label}</p>
              <p className="text-[10px] text-[#1B2A22]/60 leading-tight">{meta.desc}</p>
            </div>
          </label>
        ))}
      </div>
      <div className="flex items-center justify-between gap-2 pt-2 border-t border-[#E8E2D5]">
        <p className="text-[11px] text-[#1B2A22]/60">
          {dirty ? (
            <span className="text-[#D9653B] font-bold">● Kaydedilmemiş değişiklik var</span>
          ) : (
            <span>Değişiklik yok</span>
          )}
        </p>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={onCancel}
            className="rounded-full h-9 px-4"
            data-testid={`edit-perms-cancel-${email}`}
          >
            <X className="w-3 h-3 mr-1" /> İptal
          </Button>
          <Button
            size="sm"
            onClick={save}
            disabled={saving || !dirty}
            className="rounded-full h-9 px-5 bg-[#2E5A36] hover:bg-[#24472B] text-white font-heading font-bold"
            data-testid={`edit-perms-save-${email}`}
          >
            {saving ? (
              <>Kaydediliyor...</>
            ) : (
              <><Save className="w-3.5 h-3.5 mr-1.5" /> Kaydet</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
